import React, { useEffect, useState } from "react";
import { request } from "src/hooks/api";
import AdminService from "src/services/admin.services";
import wallet from "../../../assets/images/wallet.png";
import FoodLoader from "src/components/Loader";
import { format } from "date-fns";
import { formatCurrency } from "src/utils";

// Enhanced Modal component with better styling
const Modal: React.FC<{ message: string; onClose: () => void }> = ({
  message,
  onClose,
}) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 backdrop-blur-sm">
    <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-xl shadow-2xl w-96 text-center border border-gray-200">
      <div className="mb-6">
        <svg
          className="w-16 h-16 mx-auto text-green-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-xl font-medium text-gray-800 mb-6">{message}</p>
      <button
        className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white font-medium py-3 px-8 rounded-lg transition-all transform hover:scale-105 shadow-md"
        onClick={onClose}
      >
        Close
      </button>
    </div>
  </div>
);

interface WalletProps {
  agentId: string;
}

interface WalletData {
  _id: string;
  agentId: string;
  balance: { amount: number; currency_code: string };
  rewards: { bonus: { registration: number } };
  created_at: string;
  updated_at: string;
}

const WalletTab: React.FC<WalletProps> = ({ agentId }) => {
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creditAmount, setCreditAmount] = useState<string>("");
  const [modalMessage, setModalMessage] = useState<string | null>(null);
  const [transactionHistory, setTransactionHistory] = useState<any[]>([]);
  const [isCrediting, setIsCrediting] = useState<boolean>(false);
  const [debitAmount, setDebitAmount] = useState("");
  const [isDebiting, setIsDebiting] = useState(false);

  const handleDebit = async () => {
    const amountToDebit = Number(debitAmount.trim());

    if (isNaN(amountToDebit)) {
      setModalMessage("Please enter a valid debit amount");
      return;
    }

    if (amountToDebit <= 0) {
      setModalMessage("Debit amount must be greater than zero");
      return;
    }

    if (!walletData || amountToDebit > walletData.balance.amount) {
      setModalMessage("Insufficient wallet balance");
      return;
    }

    setIsDebiting(true);

    try {
      const response = await request(
        "/agents/wallet-debit",
        "PATCH",
        { amount: amountToDebit, agentId },
        true,
        false,
        false
      );
      if (
        response &&
        response.message === "wallet updated successfully. Check wallet balance"
      ) {
        setWalletData((prev) => {
          if (prev) {
            return {
              ...prev,
              balance: {
                ...prev.balance,
                amount: prev.balance.amount - amountToDebit,
              },
            };
          }
          return prev;
        });
        const currentDate = new Date();
        const newTransaction = {
          type: "debit",
          amount: amountToDebit,
          date: format(currentDate, "PPpp"),
          status: "completed",
        };
        const updatedHistory = [...transactionHistory, newTransaction];
        setTransactionHistory(updatedHistory);
        localStorage.setItem(
          `transactionHistory_${agentId}`,
          JSON.stringify(updatedHistory)
        );
        setDebitAmount("");
        setModalMessage(
          `Successfully debited ₦${amountToDebit} from agent's wallet!`
        );
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error processing debit";
      setModalMessage(errorMessage);
    } finally {
      setIsDebiting(false);
    }
  };

  const adminService = new AdminService();

  useEffect(() => {
    const fetchAgentsWallet = async () => {
      setLoading(true);
      try {
        const response = await request(
          `/agents/wallet/${agentId}`,
          "GET",
          {},
          true,
          false,
          false
        );

        if (response && response.balance) {
          setWalletData(response);
          const storedHistory = localStorage.getItem(
            `transactionHistory_${agentId}`
          );
          if (storedHistory) {
            setTransactionHistory(JSON.parse(storedHistory));
          }
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error fetching wallet details:", error);
        setError("Error fetching wallet information");
      } finally {
        setLoading(false);
      }
    };

    if (agentId) {
      fetchAgentsWallet();
    } else {
      setError("Invalid agentId");
      setLoading(false);
    }
  }, [agentId]);

  const handleCredit = async () => {
    const amountToCredit = Number(creditAmount.trim());

    if (isNaN(amountToCredit)) {
      setModalMessage("Please enter a valid credit amount");
      return;
    }

    if (amountToCredit <= 0) {
      setModalMessage("Credit amount must be greater than zero");
      return;
    }

    setIsCrediting(true);

    try {
      const response = await request(
        "/agents/wallet/credit",
        "PATCH",
        { amount: amountToCredit, agentId },
        true,
        false,
        false
      );
      if (response && response.message === "wallet updated successfully") {
        setWalletData((prev) => {
          if (prev) {
            return {
              ...prev,
              balance: {
                ...prev.balance,
                amount: prev.balance.amount + amountToCredit,
              },
            };
          }
          return prev;
        });
        const currentDate = new Date();
        const newTransaction = {
          type: "credit",
          amount: amountToCredit,
          date: format(currentDate, "PPpp"),
          status: "completed",
        };
        const updatedHistory = [...transactionHistory, newTransaction];
        setTransactionHistory(updatedHistory);
        localStorage.setItem(
          `transactionHistory_${agentId}`,
          JSON.stringify(updatedHistory)
        );

        setModalMessage(
          `Successfully credited ₦${amountToCredit} to agent's wallet!`
        );
        setCreditAmount("");
      } else {
        throw new Error("Credit operation failed");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error processing credit";
      setModalMessage(errorMessage);
    } finally {
      setIsCrediting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-[70vh] w-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <FoodLoader
          size="lg"
          text="Fetching Wallet Details"
          theme="amber"
          className="scale-90"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!walletData) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            No Wallet Data
          </h3>
          <p className="text-gray-600">
            No wallet information available for this agent
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-amber-600 to-amber-500 rounded-xl shadow-lg overflow-hidden mb-8 text-white">
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                <img src={wallet} alt="Wallet Icon" className="w-12 h-12" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold">
                  Agent Wallet Dashboard
                </h2>
                <p className="text-amber-100">
                  Manage agent's wallet transactions
                </p>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
              <p className="text-sm font-medium">
                Agent ID: {walletData.agentId}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Wallet Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transform transition-all hover:shadow-lg hover:-translate-y-1">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Current Balance
                      </p>
                      <h3 className="text-3xl font-bold text-gray-800 mt-1">
                        {formatCurrency(walletData.balance.amount ?? "")}
                      </h3>
                    </div>
                    <div className="bg-amber-100 p-3 rounded-full">
                      <svg
                        className="w-8 h-8 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Last updated: {format(walletData.updated_at, "PPpp")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transform transition-all hover:shadow-lg hover:-translate-y-1">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Registration Bonus
                      </p>
                      <h3 className="text-3xl font-bold text-gray-800 mt-1">
                        {formatCurrency(
                          walletData.rewards.bonus.registration ?? ""
                        )}
                      </h3>
                    </div>
                    <div className="bg-green-100 p-3 rounded-full">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 14l3-3m0 0l3 3m-3-3v6m-3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Wallet created: {format(walletData.created_at, "PPpp")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Credit Wallet Section */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transform transition-all hover:shadow-lg">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 p-2 rounded-lg mr-3">
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      Credit Wallet
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="creditAmount"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Amount to Credit
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500">₦</span>
                        </div>
                        <input
                          id="creditAmount"
                          type="number"
                          value={creditAmount}
                          onChange={(e) => setCreditAmount(e.target.value)}
                          placeholder="0.00"
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleCredit}
                      disabled={isCrediting}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-[1.01] shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isCrediting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Credit Wallet
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Debit Wallet Section */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transform transition-all hover:shadow-lg">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-red-100 p-2 rounded-lg mr-3">
                      <svg
                        className="w-6 h-6 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      Debit Wallet
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="debitAmount"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Amount to Debit
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500">₦</span>
                        </div>
                        <input
                          id="debitAmount"
                          type="number"
                          value={debitAmount}
                          onChange={(e) => setDebitAmount(e.target.value)}
                          placeholder="0.00"
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleDebit}
                      disabled={isDebiting}
                      className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-[1.01] shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isDebiting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Debit Wallet
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction History */}
            {transactionHistory.length > 0 && (
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-800">
                      Recent Transactions
                    </h3>
                    {transactionHistory.length > 5 && (
                      <button className="text-sm font-medium text-amber-600 hover:text-amber-800">
                        View All
                      </button>
                    )}
                  </div>
                  <div className="space-y-4">
                    {transactionHistory
                      .slice(0, 5)
                      .map((transaction, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="flex items-center">
                            <div
                              className={`p-3 rounded-full ${
                                transaction.type === "credit"
                                  ? "bg-green-100 text-green-600"
                                  : "bg-red-100 text-red-600"
                              }`}
                            >
                              {transaction.type === "credit" ? (
                                <svg
                                  className="w-6 h-6"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-6 h-6"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20 12H4"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="ml-4">
                              <h4 className="font-medium text-gray-800 capitalize">
                                {transaction.type}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {transaction.date}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={`font-semibold ${
                                transaction.type === "credit"
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {transaction.type === "credit" ? "+" : "-"}
                              {formatCurrency(transaction.amount ?? "")}
                            </p>
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                transaction.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {transaction.status}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Wallet Details */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Wallet Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Wallet ID
                    </p>
                    <p className="text-gray-800 font-mono break-all">
                      {walletData._id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Agent ID
                    </p>
                    <p className="text-gray-800">{walletData.agentId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Currency
                    </p>
                    <p className="text-gray-800 uppercase">
                      {walletData.balance.currency_code}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Created</p>
                    <p className="text-gray-800">
                      {format(walletData.created_at, "PPpp")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Last Updated
                    </p>
                    <p className="text-gray-800">
                      {format(walletData.updated_at, "PPpp")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalMessage && (
        <Modal message={modalMessage} onClose={() => setModalMessage(null)} />
      )}
    </div>
  );
};

export default WalletTab;
